// BoothAmenitiesManager Component (Issue #1028 - Phase 2B)
// Amenity marker placement and management for facility locations on booth map
// Supports 10 amenity types with drag-and-drop placement and icon selection

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Save, X, Plus, Trash2, Edit2, AlertCircle, Loader2 } from 'lucide-react';
import BoothMapCanvas from './BoothMapCanvas';
import AmenityMarker from './AmenityMarker';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { fairManagerApi } from '../services/api';
import {
  BoothAmenity,
  BoothAmenityCreate,
  BoothAmenityUpdate,
  AmenityType,
} from '../types';
import { extractApiError } from '../utils/errorUtils';
import { ROUTES } from '../constants/routes';

interface EditableBoothAmenity extends BoothAmenity {
  isSelected?: boolean;
  isDragging?: boolean;
}

// Amenity type icons and labels (Issue #760)
const AMENITY_TYPES: Array<{
  type: AmenityType;
  label: string;
  icon: string;
  color: string;
}> = [
  { type: 'restroom', label: 'Restroom', icon: '🚻', color: '#3B82F6' },
  { type: 'exit', label: 'Exit', icon: '🚪', color: '#EF4444' },
  { type: 'electrical', label: 'Electrical', icon: '⚡', color: '#F59E0B' },
  { type: 'water', label: 'Water', icon: '💧', color: '#06B6D4' },
  { type: 'food', label: 'Food', icon: '🍕', color: '#F97316' },
  { type: 'parking', label: 'Parking', icon: '🅿️', color: '#6366F1' },
  { type: 'info', label: 'Info', icon: 'ℹ️', color: '#10B981' },
  { type: 'first_aid', label: 'First Aid', icon: '🏥', color: '#DC2626' },
  { type: 'wifi', label: 'WiFi', icon: '📶', color: '#8B5CF6' },
  { type: 'other', label: 'Other', icon: '📍', color: '#64748B' },
];

const BoothAmenitiesManager: React.FC = () => {
  const { fairId } = useParams<{ fairId: string }>();
  const navigate = useNavigate();
  const { isEnabled: boothMapEnabled, isLoading: featureFlagLoading } = useFeatureFlag('booth_map_enabled');

  // State
  const [amenities, setAmenities] = useState<EditableBoothAmenity[]>([]);
  const [selectedAmenityId, setSelectedAmenityId] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Create amenity form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAmenityName, setNewAmenityName] = useState('');
  const [newAmenityType, setNewAmenityType] = useState<AmenityType>('info');
  const [newAmenityIcon, setNewAmenityIcon] = useState('ℹ️');

  // Edit amenity form state
  const [showEditForm, setShowEditForm] = useState(false);
  const [editAmenityName, setEditAmenityName] = useState('');
  const [editAmenityType, setEditAmenityType] = useState<AmenityType>('info');
  const [editAmenityIcon, setEditAmenityIcon] = useState('ℹ️');

  // Ref to the transformed content group for coordinate conversion
  const contentGroupRef = useRef<SVGGElement>(null);

  // Ref to store latest drag position (prevents stale coordinates on auto-save)
  const lastDragPositionRef = useRef<{ x: number; y: number } | null>(null);

  // Fetch booth amenities on mount
  useEffect(() => {
    if (!fairId) return;

    const fetchData = async () => {
      console.debug('[BoothAmenitiesManager] Fetching booth amenities', { fairId });
      setIsLoading(true);
      setError(null);

      try {
        // Fetch booth amenities (may be empty for new fairs)
        const amenitiesResponse = await fairManagerApi.getBoothAmenities(parseInt(fairId));
        console.info('[BoothAmenitiesManager] Booth amenities loaded', { count: amenitiesResponse.length });

        const editableAmenities = amenitiesResponse.map((amenity) => ({
          ...amenity,
          isSelected: false,
          isDragging: false,
        }));

        setAmenities(editableAmenities);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('[BoothAmenitiesManager] Failed to load booth amenities', apiError);
        setError(apiError.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [fairId]);

  // Handle amenity selection
  const handleAmenityClick = (amenityId: number) => {
    console.info('[BoothAmenitiesManager] Amenity selected', { amenityId });
    setSelectedAmenityId(amenityId);
    setAmenities((prev) =>
      prev.map((amenity) => ({
        ...amenity,
        isSelected: amenity.id === amenityId,
      }))
    );

    // Populate edit form
    const amenity = amenities.find((a) => a.id === amenityId);
    if (amenity) {
      setEditAmenityName(amenity.name);
      setEditAmenityType(amenity.type);
      setEditAmenityIcon(amenity.icon || AMENITY_TYPES.find((t) => t.type === amenity.type)?.icon || '📍');
    }
  };

  // Handle amenity drag start (supports both mouse and touch)
  const handleAmenityDragStart = (e: React.MouseEvent | React.TouchEvent, amenityId: number) => {
    console.debug('[BoothAmenitiesManager] Amenity drag start', { amenityId });
    e.stopPropagation();
    setIsDragging(true);
    setSelectedAmenityId(amenityId);

    const amenity = amenities.find((a) => a.id === amenityId);
    if (amenity) {
      // Sync edit state with the amenity being dragged (prevents stale data on auto-save)
      const amenityInfo = AMENITY_TYPES.find((t) => t.type === amenity.type);
      setEditAmenityName(amenity.name);
      setEditAmenityType(amenity.type);
      setEditAmenityIcon(amenity.icon || amenityInfo?.icon || '📍');

      // Extract client coordinates (mouse or touch)
      const clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
      const clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;

      // Calculate offset between pointer and amenity position
      // Use the content group's CTM to account for zoom/pan transform
      const svg = (e.target as SVGElement).ownerSVGElement;
      const contentGroup = contentGroupRef.current;
      if (svg && contentGroup) {
        const ctm = contentGroup.getScreenCTM();
        if (ctm) {
          const pt = svg.createSVGPoint();
          pt.x = clientX;
          pt.y = clientY;
          // Transform screen coordinates to map coordinates using content group's CTM
          const svgP = pt.matrixTransform(ctm.inverse());
          setDragOffset({
            x: svgP.x - amenity.x_coord,
            y: svgP.y - amenity.y_coord,
          });
        }
      }

      setAmenities((prev) =>
        prev.map((a) => ({
          ...a,
          isDragging: a.id === amenityId,
          isSelected: a.id === amenityId,
        }))
      );
    }
  };

  // Handle amenity drag move (supports both mouse and touch)
  const handleAmenityDragMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging || selectedAmenityId === null) return;

    // Extract client coordinates (mouse or touch)
    const clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
    const clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;

    // Get SVG from contentGroupRef to avoid crashes when cursor leaves SVG
    const contentGroup = contentGroupRef.current;
    const svg = contentGroup?.ownerSVGElement;
    if (!svg || !contentGroup) return;

    // Guard getScreenCTM() to avoid null dereference if SVG is temporarily detached
    const ctm = contentGroup.getScreenCTM();
    if (!ctm) return;

    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    // Transform screen coordinates to map coordinates using content group's CTM
    const svgP = pt.matrixTransform(ctm.inverse());

    // Update amenity position (with grid snapping if enabled)
    setAmenities((prev) =>
      prev.map((amenity) => {
        if (amenity.id === selectedAmenityId) {
          const newX = Math.max(0, svgP.x - dragOffset.x);
          const newY = Math.max(0, svgP.y - dragOffset.y);

          // Snap to grid (10px increments)
          const snappedX = Math.round(newX / 10) * 10;
          const snappedY = Math.round(newY / 10) * 10;

          // Store latest position in ref for auto-save (prevents stale state reads)
          lastDragPositionRef.current = { x: snappedX, y: snappedY };

          return {
            ...amenity,
            x_coord: snappedX,
            y_coord: snappedY,
          };
        }
        return amenity;
      })
    );
  }, [isDragging, selectedAmenityId, dragOffset]);

  // Save position only (called after drag - doesn't persist unsaved name/type/icon edits)
  const handleSavePositionOnly = useCallback(async () => {
    if (selectedAmenityId === null) return;

    const amenity = amenities.find((a) => a.id === selectedAmenityId);
    if (!amenity) return;

    console.info('[BoothAmenitiesManager] Saving amenity position only', { amenityId: selectedAmenityId });

    try {
      // Use ref coordinates if available (from recent drag), otherwise use state
      const coords = lastDragPositionRef.current || { x: amenity.x_coord, y: amenity.y_coord };
      lastDragPositionRef.current = null; // Clear ref after use

      // Only send position - don't persist unsaved edits to name/type/icon
      const updateData: BoothAmenityUpdate = {
        x_coord: coords.x,
        y_coord: coords.y,
      };

      await fairManagerApi.updateBoothAmenity(selectedAmenityId, updateData);
      console.info('[BoothAmenitiesManager] Amenity position saved successfully');
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('[BoothAmenitiesManager] Failed to save amenity position', apiError);
      setError(apiError.message);
    }
  }, [selectedAmenityId, amenities]);

  // Handle amenity drag end
  const handleAmenityDragEnd = useCallback(() => {
    console.debug('[BoothAmenitiesManager] Amenity drag end');
    setIsDragging(false);
    setAmenities((prev) =>
      prev.map((amenity) => ({
        ...amenity,
        isDragging: false,
      }))
    );

    // Auto-save position only if amenity actually moved (prevents unnecessary API writes on click-release)
    if (selectedAmenityId !== null && lastDragPositionRef.current !== null) {
      handleSavePositionOnly();
    }
  }, [selectedAmenityId, handleSavePositionOnly]);

  // Attach global mouse/touch event listeners for drag (prevents losing drag when pointer leaves element)
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        const mouseEvent = e as unknown as React.MouseEvent<SVGSVGElement>;
        handleAmenityDragMove(mouseEvent);
      };

      const handleGlobalTouchMove = (e: TouchEvent) => {
        const touchEvent = e as unknown as React.TouchEvent<SVGSVGElement>;
        handleAmenityDragMove(touchEvent);
      };

      const handleGlobalEnd = () => {
        handleAmenityDragEnd();
      };

      window.addEventListener('mousemove', handleGlobalMouseMove);
      window.addEventListener('mouseup', handleGlobalEnd);
      window.addEventListener('touchmove', handleGlobalTouchMove);
      window.addEventListener('touchend', handleGlobalEnd);
      window.addEventListener('touchcancel', handleGlobalEnd);

      return () => {
        window.removeEventListener('mousemove', handleGlobalMouseMove);
        window.removeEventListener('mouseup', handleGlobalEnd);
        window.removeEventListener('touchmove', handleGlobalTouchMove);
        window.removeEventListener('touchend', handleGlobalEnd);
        window.removeEventListener('touchcancel', handleGlobalEnd);
      };
    }
  }, [isDragging, selectedAmenityId, dragOffset, handleAmenityDragMove, handleAmenityDragEnd]);

  // Handle create amenity
  const handleCreateAmenity = async () => {
    if (!fairId) return;

    // Validate name (required, backend will sanitize)
    const trimmedName = newAmenityName.trim();
    if (!trimmedName) {
      setError('Amenity name is required');
      return;
    }

    console.info('[BoothAmenitiesManager] Creating amenity', { name: trimmedName, type: newAmenityType });
    setIsSaving(true);
    setError(null);

    try {
      const createData: Omit<BoothAmenityCreate, 'fair_id'> = {
        name: trimmedName,
        type: newAmenityType,
        x_coord: 250, // Center of canvas (default)
        y_coord: 200,
        icon: newAmenityIcon,
      };

      const newAmenity = await fairManagerApi.createBoothAmenity(parseInt(fairId), createData);
      console.info('[BoothAmenitiesManager] Amenity created successfully', { amenityId: newAmenity.id });
      setSuccessMessage('Amenity created successfully');
      setTimeout(() => setSuccessMessage(null), 3000);

      // Add to list
      setAmenities((prev) => [...prev, { ...newAmenity, isSelected: false, isDragging: false }]);

      // Reset form
      setNewAmenityName('');
      setNewAmenityType('info');
      setNewAmenityIcon('ℹ️');
      setShowCreateForm(false);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('[BoothAmenitiesManager] Failed to create amenity', apiError);
      setError(apiError.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Save full amenity (name, type, icon, position)
  const handleSaveAmenity = async () => {
    if (selectedAmenityId === null) return;

    const amenity = amenities.find((a) => a.id === selectedAmenityId);
    if (!amenity) return;

    // Validate name (required, backend will sanitize)
    const trimmedName = editAmenityName.trim();
    if (!trimmedName) {
      setError('Amenity name is required');
      return;
    }

    console.info('[BoothAmenitiesManager] Saving full amenity', { amenityId: selectedAmenityId });
    setIsSaving(true);
    setError(null);

    try {
      // Use ref coordinates if available (from recent drag), otherwise use state
      const coords = lastDragPositionRef.current || { x: amenity.x_coord, y: amenity.y_coord };
      lastDragPositionRef.current = null; // Clear ref after use

      const updateData: BoothAmenityUpdate = {
        name: trimmedName,
        type: editAmenityType,
        x_coord: coords.x,
        y_coord: coords.y,
        icon: editAmenityIcon,
      };

      await fairManagerApi.updateBoothAmenity(selectedAmenityId, updateData);
      console.info('[BoothAmenitiesManager] Amenity saved successfully');
      setSuccessMessage('Amenity saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);

      // Update in list
      setAmenities((prev) =>
        prev.map((a) =>
          a.id === selectedAmenityId
            ? { ...a, name: updateData.name!, type: updateData.type!, icon: updateData.icon }
            : a
        )
      );

      setShowEditForm(false);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('[BoothAmenitiesManager] Failed to save amenity', apiError);
      setError(apiError.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete amenity
  const handleDeleteAmenity = async (amenityId: number) => {
    if (!confirm('Are you sure you want to delete this amenity? This action cannot be undone.')) {
      return;
    }

    console.info('[BoothAmenitiesManager] Deleting amenity', { amenityId });
    setIsSaving(true);
    setError(null);

    try {
      await fairManagerApi.deleteBoothAmenity(amenityId);
      console.info('[BoothAmenitiesManager] Amenity deleted successfully');
      setSuccessMessage('Amenity deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);

      // Remove from list
      setAmenities((prev) => prev.filter((a) => a.id !== amenityId));
      setSelectedAmenityId(null);
      setShowEditForm(false);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('[BoothAmenitiesManager] Failed to delete amenity', apiError);
      setError(apiError.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle amenity type change (updates icon automatically)
  const handleTypeChange = (type: AmenityType, isEditMode: boolean) => {
    const amenityInfo = AMENITY_TYPES.find((t) => t.type === type);
    if (amenityInfo) {
      if (isEditMode) {
        setEditAmenityType(type);
        setEditAmenityIcon(amenityInfo.icon);
      } else {
        setNewAmenityType(type);
        setNewAmenityIcon(amenityInfo.icon);
      }
    }
  };

  // Render loading state
  if (featureFlagLoading || isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  // Render feature flag disabled state
  if (!boothMapEnabled) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Booth map features are not yet available.</p>
        <button
          onClick={() => navigate(ROUTES.FAIR_MANAGER.FAIRS)}
          className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
        >
          Back to Fairs List
        </button>
      </div>
    );
  }

  const selectedAmenity = amenities.find((a) => a.id === selectedAmenityId);

  return (
    <div className="booth-amenities-manager container mx-auto px-4 py-8" data-testid="booth-amenities-manager-container">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="page-title">
            Booth Amenities Manager
          </h1>
          <p className="text-gray-600 mt-1">
            Add and position amenity markers on the map. Drag markers to reposition them.
          </p>
        </div>
        <button
          onClick={() => navigate(ROUTES.FAIR_MANAGER.FAIRS)}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center gap-2"
          data-testid="back-button"
        >
          <X className="h-4 w-4" />
          Back to Fairs List
        </button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center gap-2" data-testid="error-message">
          <AlertCircle className="h-5 w-5" />
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded" data-testid="success-message">
          {successMessage}
        </div>
      )}

      {/* Create Amenity Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2"
          data-testid="create-amenity-button"
        >
          <Plus className="h-4 w-4" />
          Add New Amenity
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Booth Map Canvas */}
        <div className="lg:col-span-3">
          <BoothMapCanvas
            editMode
            showGrid
            showControls
            className="mb-4"
            contentGroupRef={contentGroupRef}
          >
            {/* Render amenities */}
            {amenities.map((amenity) => {
              const amenityInfo = AMENITY_TYPES.find((t) => t.type === amenity.type);
              // Map AmenityType to AmenityMarker type (direct mapping with fallbacks)
              let markerType: 'restroom' | 'entrance' | 'food_court' | 'parking' | 'stage' | 'seating' | 'water' | 'waste' | 'exit' | 'electrical' | 'info' | 'first_aid' | 'wifi' | 'other';
              switch (amenity.type) {
                case 'restroom':
                  markerType = 'restroom';
                  break;
                case 'exit':
                  markerType = 'exit';
                  break;
                case 'food':
                  markerType = 'food_court';
                  break;
                case 'parking':
                  markerType = 'parking';
                  break;
                case 'water':
                  markerType = 'water';
                  break;
                case 'first_aid':
                  markerType = 'first_aid';
                  break;
                case 'electrical':
                  markerType = 'electrical';
                  break;
                case 'info':
                  markerType = 'info';
                  break;
                case 'wifi':
                  markerType = 'wifi';
                  break;
                case 'other':
                  markerType = 'other';
                  break;
                default:
                  markerType = 'other'; // Fallback for unknown types
                  break;
              }

              return (
                <g
                  key={amenity.id}
                  onMouseDown={(e) => handleAmenityDragStart(e, amenity.id)}
                  onTouchStart={(e) => handleAmenityDragStart(e, amenity.id)}
                  onClick={() => handleAmenityClick(amenity.id)}
                  className="amenity-marker-group cursor-move"
                  data-amenity-id={amenity.id}
                  data-testid={`amenity-${amenity.id}`}
                >
                  {/* Render amenity marker */}
                  <AmenityMarker
                    name={amenity.name}
                    type={markerType}
                    x={amenity.x_coord}
                    y={amenity.y_coord}
                    icon={amenity.icon || amenityInfo?.icon || '📍'}
                    scale={amenity.isSelected ? 1.2 : 1}
                  />

                  {/* Selection ring */}
                  {amenity.isSelected && (
                    <circle
                      cx={amenity.x_coord}
                      cy={amenity.y_coord}
                      r={20}
                      fill="none"
                      stroke="#1F2937"
                      strokeWidth={3}
                      strokeDasharray="5,5"
                      className="pointer-events-none"
                    />
                  )}
                </g>
              );
            })}
          </BoothMapCanvas>

          {amenities.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>No amenities found. Click "Add New Amenity" to get started.</p>
            </div>
          )}
        </div>

        {/* Amenity Editor Panel */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-4 sticky top-4" data-testid="amenities-controls">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Amenity Editor</h2>

            {/* Create Form */}
            {showCreateForm && (
              <div className="space-y-4 mb-6 pb-6 border-b border-gray-200" data-testid="amenity-create-form">
                <h3 className="text-md font-semibold text-gray-700">Create New Amenity</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={newAmenityName}
                    onChange={(e) => setNewAmenityName(e.target.value)}
                    placeholder="e.g., North Restroom"
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    data-testid="amenity-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <select
                    value={newAmenityType}
                    onChange={(e) => handleTypeChange(e.target.value as AmenityType, false)}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    data-testid="amenity-type-select"
                  >
                    {AMENITY_TYPES.map((type) => (
                      <option key={type.type} value={type.type}>
                        {type.icon} {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                  <input
                    type="text"
                    value={newAmenityIcon}
                    onChange={(e) => setNewAmenityIcon(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-2xl text-center"
                    maxLength={2}
                    data-testid="amenity-icon-input"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleCreateAmenity}
                    disabled={isSaving}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
                    data-testid="save-amenity-button"
                  >
                    <Save className="h-4 w-4" />
                    {isSaving ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewAmenityName('');
                      setNewAmenityType('info');
                      setNewAmenityIcon('ℹ️');
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    data-testid="create-cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Edit Form */}
            {selectedAmenity && showEditForm && (
              <div className="space-y-4 mb-6 pb-6 border-b border-gray-200">
                <h3 className="text-md font-semibold text-gray-700">Edit Amenity</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={editAmenityName}
                    onChange={(e) => setEditAmenityName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    data-testid="edit-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <select
                    value={editAmenityType}
                    onChange={(e) => handleTypeChange(e.target.value as AmenityType, true)}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    data-testid="edit-type-select"
                  >
                    {AMENITY_TYPES.map((type) => (
                      <option key={type.type} value={type.type}>
                        {type.icon} {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                  <input
                    type="text"
                    value={editAmenityIcon}
                    onChange={(e) => setEditAmenityIcon(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-2xl text-center"
                    maxLength={2}
                    data-testid="edit-icon-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      value={Math.round(selectedAmenity.x_coord)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50"
                      data-testid="edit-x-coord"
                    />
                    <input
                      type="number"
                      value={Math.round(selectedAmenity.y_coord)}
                      readOnly
                      className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-50"
                      data-testid="edit-y-coord"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Drag marker on map to reposition</p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleSaveAmenity}
                    disabled={isSaving}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark disabled:bg-gray-400 flex items-center justify-center gap-2"
                    data-testid="edit-save-button"
                  >
                    <Save className="h-4 w-4" />
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setShowEditForm(false)}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    data-testid="edit-cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Selected Amenity Info (when not editing) */}
            {selectedAmenity && !showEditForm ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Selected Amenity</label>
                  <p className="text-lg font-semibold">{selectedAmenity.name}</p>
                  <p className="text-sm text-gray-500">
                    {AMENITY_TYPES.find((t) => t.type === selectedAmenity.type)?.label}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                  <p className="text-sm text-gray-600">
                    X: {Math.round(selectedAmenity.x_coord)}, Y: {Math.round(selectedAmenity.y_coord)}
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setShowEditForm(true)}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark flex items-center justify-center gap-2"
                    data-testid="edit-button"
                  >
                    <Edit2 className="h-4 w-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteAmenity(selectedAmenity.id)}
                    disabled={isSaving}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
                    data-testid="delete-button"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </div>
            ) : !selectedAmenity && !showCreateForm ? (
              <div className="text-center text-gray-500 py-8">
                <MapPin className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Select an amenity or create a new one</p>
              </div>
            ) : null}

            {/* Amenity List */}
            {amenities.length > 0 && !showCreateForm && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-md font-semibold text-gray-700 mb-3">All Amenities</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {amenities.map((amenity) => {
                    const amenityInfo = AMENITY_TYPES.find((t) => t.type === amenity.type);
                    return (
                      <button
                        key={amenity.id}
                        onClick={() => handleAmenityClick(amenity.id)}
                        className={`w-full text-left px-3 py-2 rounded border ${
                          amenity.isSelected
                            ? 'bg-moonbone border-dusty-teal'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                        data-testid={`amenity-list-item-${amenity.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{amenity.icon || amenityInfo?.icon}</span>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{amenity.name}</p>
                            <p className="text-xs text-gray-500">{amenityInfo?.label}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoothAmenitiesManager;
